package com.essntl.features.file.ui

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.file.domain.model.FileModel
import com.essntl.features.file.domain.repository.FileRepository
import com.mohamedrejeb.calf.core.PlatformContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class FileViewModel(
    private val fileRepository: FileRepository,
    val state: MutableStateFlow<FileState> = MutableStateFlow(FileState()),
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {
    fun getFiles(
        ids: List<String>,
        order: Boolean = false,
    ) {
        if (order)
            scope.launch {
                val files = mutableListOf<FileModel>()

                for (id in ids) {
                    val result = async {
                        fileRepository.getById(id)
                    }.await()

                    result.onSuccess { fileModel ->
                        files.add(fileModel)
                    }
                }

                if (files.isNotEmpty()) addFiles(files)
            }
        else
            getData(
                getData = { fileRepository.getByIds(ids) },
                getState = { state.value.getAllFile },
                setState = { newState ->
                    scope.launch {
                        state.emit(
                            state.value.copy(
                                getAllFile = newState,
                            ),
                        )
                    }
                },
            )
    }

    fun addFiles(files: List<FileModel>) =
        scope.launch {
            state.emit(
                state.value.copy(
                    getAllFile = state.value.getAllFile.copy(
                        data = state.value.getAllFile.data.orEmpty() + files,
                    ),
                ),
            )
        }

    fun deleteFile(fileIndex: Int) =
        scope.launch {
            val fileModel = state.value.getAllFile.data.orEmpty().getOrNull(fileIndex) ?: return@launch
            state.emit(
                state.value.copy(
                    getAllFile = State(
                        data = state.value.getAllFile.data?.filterIndexed { index, _ ->
                            index != fileIndex
                        },
                    ),
                    deleteFileState = DeleteFileState(
                        files = state.value.deleteFileState.files + fileModel,
                    ),
                ),
            )
        }


    /**
     * Insert files into the database
     * @param fileUrlList The list of files to insert
     * @return true if the operation was successful, false otherwise
     */
    suspend fun insertFiles(
        folderName: String,
        bucketName: String,
        entityType: String,
        keywords: List<String> = emptyList(),
        context: PlatformContext,
        onSuccess: (Set<FileModel>) -> Unit,
        onFailure: () -> Unit,
    ): Boolean {
        val filesToInsert =
            state.value.getAllFile.data
                .orEmpty()
                .filter { fileModel ->
                    fileModel.id.isEmpty()
                }.map { fileModel ->
                    fileModel.copy(
                        entityType = entityType,
                        keywords = if (keywords.isNotEmpty())
                            keywords else
                            fileModel.keywords,
                    )
                }

        if (filesToInsert.isEmpty()) return true

        val result = fileRepository.insertAll(filesToInsert, folderName, bucketName, context)
            .onSuccess { fileModel ->
                state.emit(
                    state.value.copy(
                        getAllFile = State(
                            data = state.value.getAllFile.data.orEmpty()
                                .filter { file -> file.id.isNotEmpty() } + fileModel,
                        ),
                    ).also {
                        onSuccess(fileModel.toMutableSet())
                    },
                )
            }
            .onFailure {
                onFailure()
            }

        return result.isSuccess
    }


    /**
     * Delete files from the database
     * @param fileUrlList The list of files to delete
     * @return true if the operation was successful, false otherwise
     */
    suspend fun deleteFiles(
        onSuccess: (List<FileModel>) -> Unit,
        onFailure: () -> Unit,
    ): Boolean {
        val filesToDelete = state.value.deleteFileState.files
            .filter { it.id.isNotEmpty() }

        state.emit(
            state.value.copy(
                deleteFileState = DeleteFileState(),
            ),
        )

        if (filesToDelete.isEmpty()) return true

        val result = fileRepository.deleteAll(filesToDelete)
            .onSuccess {
                onSuccess(filesToDelete)
            }
            .onFailure {
                onFailure()
            }

        return result.isSuccess
    }


    fun setState(
        getAllFileState:
        State<List<FileModel>>,
    ) =
        scope.launch {
            state.emit(
                state.value.copy(
                    getAllFile = getAllFileState,
                ),
            )
        }

    fun insertOrganizationLogos(
        setState: (State<Unit>) -> Unit,
        onSuccess: (Set<FileModel>) -> Unit = {},
        onFailure: () -> Unit = {},
        folderName: String,
        entityType: String,
        organizationName: String,
        context: PlatformContext,
    ) {
        scope.launch {
            val keywords =
                if (organizationName.isNotEmpty())
                    listOf(organizationName) else
                    emptyList()

            setState(State.loading())

            val upload =
                insertFiles(
                    folderName = folderName,
                    entityType = entityType,
                    bucketName = "images",
                    keywords = keywords,
                    context = context,
                    onSuccess = { files -> onSuccess(files) },
                    onFailure = {
                        onFailure()
                        println("Failed to insert files")
                    },
                )

            setState(
                if (upload)
                    State.success(Unit) else
                    State.failure(),
            )
        }
    }
}